import { useEffect, useMemo, useState } from 'react';
import { api } from './api.js';
import { changesFeed } from './changes-feed.js';

/** Watch is on for this tenant: in the plan, switched on and not suspended by an operator. */
export function watchIsOn(watch) {
  return Boolean(watch) && watch.tierAllowed !== false && watch.suspended !== true && watch.enabled === true;
}

const EMPTY = Object.freeze({ rows: [], total: 0 });

/**
 * "Changes since yesterday" for a customer (ENTRAID-347 redesign, B2 on D2): the server computes the feed
 * (GET /customers/{id}/changes) with the same rules for every client, and names the directory role of a Watch role
 * event ("New Global Administrator"). The server decides who sees Watch content (the customer's own admin, never an
 * operator account); `watchAllowed` only decides whether the "Turn on Watch" hint may show.
 *
 * If the endpoint cannot be read, the feed falls back to the client rules over `runs` (posture and collection rows
 * only, never Watch), so the card is never empty because of a transient error.
 *
 * `mode: 'last-score-change'` asks the server for "Since the last score change" (ENTRAID-347): `lastScoreChange` then
 * explains the newest score change (or since when the score is unchanged); it is null on the fallback.
 *
 * @returns {{ rows: object[], total: number, watchHint: boolean, watchStatus: 'idle'|'loading'|'on'|'off'|'error', lastScoreChange: object|null }}
 */
export function useChangesSinceYesterday(customerId, { runs = [], watchAllowed = false, enabled = true, mode = null } = {}) {
  const [state, setState] = useState({ key: null, status: 'idle', data: null });
  const key = `${customerId}|${enabled}|${mode ?? ''}`;

  useEffect(() => {
    if (!customerId || !enabled) { setState({ key, status: 'idle', data: null }); return undefined; }
    let live = true;
    setState({ key, status: 'loading', data: null });
    // Promise.resolve first: a missing or throwing client is a rejection (the fallback), never a render error.
    Promise.resolve()
      .then(() => (mode ? api.getChanges(customerId, { mode }) : api.getChanges(customerId)))
      .then(
        (data) => { if (live) setState({ key, status: data && Array.isArray(data.rows) ? 'ready' : 'error', data: data ?? null }); },
        () => { if (live) setState({ key, status: 'error', data: null }); },
      );
    return () => { live = false; };
  }, [customerId, enabled, mode, key]);

  const current = state.key === key ? state : { status: 'loading', data: null };
  const fallback = useMemo(() => (current.status === 'error' ? changesFeed({ runs, events: null }) : EMPTY), [current.status, runs]);
  if (current.status === 'ready') {
    const watchStatus = watchAllowed ? (current.data.watch?.status ?? 'idle') : 'idle';
    return { rows: current.data.rows, total: current.data.total ?? current.data.rows.length, watchHint: watchAllowed && watchStatus === 'off', watchStatus, lastScoreChange: current.data.lastScoreChange ?? null };
  }
  return { ...fallback, watchHint: false, watchStatus: watchAllowed ? (current.status === 'error' ? 'error' : 'loading') : 'idle', lastScoreChange: null };
}
